import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  Typography,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import React from "react";
import { LoadingButton } from "@mui/lab";
import useFetch, { CachePolicies } from "use-http";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { LedgerTransactionApiRow } from "../../../routes/Finances/LedgerTransactionsPage";
import { toCurrencyString } from "../../../utils/finance";

type MergeTransactionDialogProps = {
  transactionToMerge: LedgerTransactionApiRow;
  relatedTransactions?: LedgerTransactionApiRow[];
  onSuccess?: () => void;
} & DialogProps;

const MergeTransactionDialog: React.FC<MergeTransactionDialogProps> = ({
  transactionToMerge,
  relatedTransactions = [],
  onSuccess,
  ...props
}) => {
  const auth = useAuth();
  const { ledgerId } = useParams();
  const { post: merge, loading: isMergeLoading } = useFetch(
    `${process.env.REACT_APP_API_BASE_URL}/ledger/${ledgerId}/transaction/${transactionToMerge.id}/merge`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
      headers: {
        Authorization: `Bearer ${auth.encodedToken}`,
      },
    }
  );

  // Add state for Snackbar
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "error" as "error" | "warning" | "info" | "success",
  });

  // Close Snackbar handler
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Show error message function
  const showErrorMessage = (message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "error",
    });
  };

  const handleMerge = React.useCallback(() => {
    merge({})
      .then(() => {
        onSuccess?.();
        props.onClose?.({}, "escapeKeyDown");
      })
      .catch((error) => {
        console.error("Erro ao mesclar transações:", error);
        showErrorMessage("Erro ao mesclar as transações.");
      });
  }, [merge, onSuccess, props]);

  return (
    <>
      <Dialog {...props} fullWidth maxWidth="md">
        <DialogTitle>Mesclar Transações</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              Ao mesclar transações, você estará desfazendo o separamento
              anteriormente realizado. Esta ação irá:
            </Typography>

            <List>
              <ListItem>
                <ListItemText primary="• Reativar a transação original que foi separada" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Remover esta transação e todas as outras que foram criadas a partir do separamento" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Todas as classificações e observações feitas nas transações separadas serão perdidas" />
              </ListItem>
            </List>

            <Typography variant="body1" fontWeight="bold">
              Esta ação não pode ser desfeita. Deseja continuar?
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Transações que serão removidas:
            </Typography>
            <List dense>
              {/* Always show the transaction to merge first */}
              <ListItem key={transactionToMerge.id}>
                <ListItemText
                  primary={transactionToMerge.description}
                  secondary={`Valor: ${
                    toCurrencyString(
                      transactionToMerge.amount,
                      transactionToMerge.currency
                    ).formattedValue
                  }`}
                />
              </ListItem>

              {/* Then show all related transactions */}
              {relatedTransactions.map((transaction) => (
                <ListItem key={transaction.id}>
                  <ListItemText
                    primary={transaction.description}
                    secondary={`Valor: ${
                      toCurrencyString(transaction.amount, transaction.currency)
                        .formattedValue
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => props.onClose?.({}, "escapeKeyDown")}>
            Cancelar
          </Button>
          <LoadingButton
            loading={isMergeLoading}
            color="warning"
            onClick={handleMerge}
          >
            Mesclar Transações
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MergeTransactionDialog;
